import { useState, useCallback } from "react";
import { cleanBaselineHtml } from "../../../util/lexical";

/**
 * useStepForm - Custom hook for managing step form state and handlers
 *
 * Consolidates all form-related state management including:
 * - Form data (basic fields)
 * - Table data (step details, BOM table)
 * - Category selections
 * - Visibility flags
 * - Editing states
 * - Modal states
 *
 * @param {Object} config
 * @param {string} config.mode - 'add' or 'edit'
 * @param {Object} config.initialData - Initial form data (for edit mode)
 * @param {function} config.onUnsavedChanges - Callback when data changes
 *
 * @returns {Object} Form state and handlers
 */
const useStepForm = ({ mode, initialData = {}, onUnsavedChanges, snackbar }) => {
  // Form data
  const [shortDescription, setShortDescription] = useState(
    initialData.shortDescription || ""
  );

  // Table data
  const [stepDetails, setStepDetails] = useState([]);
  const [bomTable, setBomTable] = useState([]);

  // Category data
  const [bomTableCategory, setBomTableCategory] = useState("");
  const [bomTableCategoryId, setBomTableCategoryId] = useState("");
  const [partNumberCategory, setPartNumberCategory] = useState("");
  const [partTableCategoryId, setPartTableCategoryId] = useState("");

  // Visibility states
  const [isHideStepHeading, setIsHideStepHeading] = useState(false);
  const [isHideStepDetailsHeading, setIsHideStepDetailsHeading] =
    useState(false);
  const [isHideBomHeading, setIsHideBomHeading] = useState(false);

  // Editing states
  const [editingStepIndex, setEditingStepIndex] = useState(null);
  const [editingBomIndex, setEditingBomIndex] = useState(null);

  // Modal states
  const [isModalOpen, setModalOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [isPartsModalOpen, setPartsModalOpen] = useState(false);
  const [selectedPartIndex, setSelectedPartIndex] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);
  const [categoryModalOpenForBom, setCategoryModalOpenForBom] = useState(false);
  const [categoryModalOpenForPart, setCategoryModalOpenForPart] =
    useState(false);

  /**
   * Handle short description change
   */
  const handleShortDescriptionChange = useCallback(
    (value) => {
      // Handle both event objects (from TextArea) and direct values (from ShortDescriptionEditor)
      const newValue = typeof value === 'object' && value.target ? value.target.value : value;
      
      // Clean the HTML content if it's from the rich text editor
      const cleanedValue = typeof newValue === 'string' && newValue.includes('<') ? 
        cleanBaselineHtml(newValue) : newValue;
      
      setShortDescription(cleanedValue);
      // NOTE: onUnsavedChanges is intentionally NOT called here.
      // Dirty detection for short description is handled via the Lexical
      // editor's undo history (CAN_UNDO_COMMAND), which is bubbled up through
      // ShortDescriptionEditor → StepForm → AddEditStep as isShortDescriptionDirty.
      // Calling onUnsavedChanges here would fire on programmatic initialization
      // (Lexical's first onChange) and produce a false-positive unsaved alert.
    },
    [] // stable – no deps needed after removing onUnsavedChanges
  );

  /**
   * Handle hide step heading change
   */
  const handleHideStepHeadingChange = useCallback(
    (e) => {
      setIsHideStepHeading(e.target.checked);
      if (onUnsavedChanges) onUnsavedChanges();
    },
    [onUnsavedChanges]
  );

  /**
   * Handle hide step details heading change
   */
  const handleHideStepDetailsHeadingChange = useCallback(
    (checked) => {
      setIsHideStepDetailsHeading(checked);
      if (onUnsavedChanges) onUnsavedChanges();
    },
    [onUnsavedChanges]
  );

  /**
   * Handle hide BOM heading change
   */
  const handleHideBomHeadingChange = useCallback(
    (checked) => {
      setIsHideBomHeading(checked);
      if (onUnsavedChanges) onUnsavedChanges();
    },
    [onUnsavedChanges]
  );

  /**
   * Handle BOM table category change
   */
  const handleBomTableCategoryChange = useCallback(
    (category) => {
      setBomTableCategoryId(category.category_id);
      setBomTableCategory(category.category_name);
      setCategoryModalOpenForBom(false);
      if (onUnsavedChanges) onUnsavedChanges();
    },
    [onUnsavedChanges]
  );

  /**
   * Handle BOM table category clear
   */
  const handleBomTableCategoryClear = useCallback(() => {
    setBomTableCategory("");
    setBomTableCategoryId("");
    if (onUnsavedChanges) onUnsavedChanges();
  }, [onUnsavedChanges]);

  /**
   * Handle part number category change
   */
  const handlePartNumberCategoryChange = useCallback(
    (category) => {
      setPartTableCategoryId(category.category_id);
      setPartNumberCategory(category.category_name);
      setCategoryModalOpenForPart(false);
      if (onUnsavedChanges) onUnsavedChanges();
    },
    [onUnsavedChanges]
  );

  /**
   * Handle part number category clear
   */
  const handlePartNumberCategoryClear = useCallback(() => {
    setPartNumberCategory("");
    setPartTableCategoryId("");
    if (onUnsavedChanges) onUnsavedChanges();
  }, [onUnsavedChanges]);

  /**
   * Handle step details change
   */
  const handleStepDetailsChange = useCallback(
    (newStepDetails) => {
      setStepDetails(newStepDetails);
      if (onUnsavedChanges) onUnsavedChanges();
    },
    [onUnsavedChanges]
  );

  /**
   * Handle BOM table change
   */
  const handleBomTableChange = useCallback(
    (newBomTable) => {
      setBomTable(newBomTable);
      if (onUnsavedChanges) onUnsavedChanges();
    },
    [onUnsavedChanges]
  );

  /**
   * Add a new row to step details table
   */
  const addStepRow = useCallback(() => {
    if (editingBomIndex !== null && editingStepIndex !== null) {
      snackbar(
        "Please save or cancel the current row before adding or editing another one.",
        "error"
      );
      return;
    }

    const hasBlankRow = stepDetails.some(
      (row) => row.isNew && !row.action && !row.partNumber && !row.qty
    );

    if (!hasBlankRow) {
      setStepDetails((prev) => [
        ...prev,
        {
          action: "",
          partNumber: "",
          qty: "",
          unit: "",
          suggestion: "",
          description: "",
          isNew: true,
        },
      ]);
      setEditingStepIndex(stepDetails.length);
    } else {
      const blankRowIndex = stepDetails.findIndex(
        (row) => row.isNew && !row.action && !row.partNumber && !row.qty
      );
      setEditingStepIndex(blankRowIndex);
    }
    if (onUnsavedChanges) onUnsavedChanges();
  }, [stepDetails, onUnsavedChanges]);

  /**
   * Add a new row to BOM table
   */
  const addBomRow = useCallback(() => {
    // Check if a row is already being edited
    if (editingBomIndex !== null && editingStepIndex !== null) {
      snackbar(
        "Please save or cancel the current row before adding or editing another one.",
        "error"
      );
      return;
    }

    const hasBlankRow = bomTable.some(
      (row) => row.isNew && !row.type && !row.partNumber && !row.qty
    );

    if (!hasBlankRow) {
      setBomTable((prev) => [
        ...prev,
        {
          type: "",
          partNumber: "",
          description: "",
          qty: "",
          unit: "",
          suggestion: "",
          isNew: true,
        },
      ]);
      setEditingBomIndex(bomTable.length);
    } else {
      const blankRowIndex = bomTable.findIndex(
        (row) => row.isNew && !row.type && !row.partNumber && !row.qty
      );
      setEditingBomIndex(blankRowIndex);
    }
    if (onUnsavedChanges) onUnsavedChanges();
  }, [bomTable, onUnsavedChanges]);

  /**
   * Handle part selection from parts modal
   */
  const handleSelectPart = useCallback(
    (selectedPart) => {
      if (selectedTable === "step") {
        setStepDetails((prevSteps) => {
          const updatedSteps = [...prevSteps];
          updatedSteps[selectedPartIndex] = {
            ...updatedSteps[selectedPartIndex],
            partNumber: selectedPart.part_number,
            partId: selectedPart.part_id,
            description: selectedPart.description || "",
            part_ref: selectedPart.part_id,
            unit_ref: selectedPart.unit_type_id_ref,
            unit: selectedPart.unit_type_id_ref,
          };
          return updatedSteps;
        });
      } else if (selectedTable === "bom") {
        setBomTable((prevBom) => {
          const updatedBom = [...prevBom];
          updatedBom[selectedPartIndex] = {
            ...updatedBom[selectedPartIndex],
            partNumber: selectedPart.part_number,
            partId: selectedPart.part_id,
            description: selectedPart.description || "",
            table_note: selectedPart.table_note || "",
            type: selectedPart.type_info?.name || "",
            part_ref: selectedPart.part_id,
            unit_ref: selectedPart.unit_type_id_ref,
            unit: selectedPart.unit_type_id_ref,
          };
          return updatedBom;
        });
      }

      setPartsModalOpen(false);
      setSelectedPartIndex(null);
      setSelectedTable(null);
      if (onUnsavedChanges) onUnsavedChanges();
    },
    [selectedTable, selectedPartIndex, onUnsavedChanges]
  );

  /**
   * Reset form to initial state
   */
  const resetForm = useCallback(() => {
    setShortDescription("");
    setStepDetails([]);
    setBomTable([]);
    setBomTableCategory("");
    setBomTableCategoryId("");
    setPartNumberCategory("");
    setPartTableCategoryId("");
    setIsHideStepHeading(false);
    setIsHideStepDetailsHeading(false);
    setIsHideBomHeading(false);
    setEditingStepIndex(null);
    setEditingBomIndex(null);
    setComment("");
  }, []);

  return {
    // Form data
    shortDescription,
    setShortDescription,

    // Table data
    stepDetails,
    setStepDetails,
    bomTable,
    setBomTable,

    // Category data
    bomTableCategory,
    setBomTableCategory,
    bomTableCategoryId,
    setBomTableCategoryId,
    partNumberCategory,
    setPartNumberCategory,
    partTableCategoryId,
    setPartTableCategoryId,

    // Visibility states
    isHideStepHeading,
    setIsHideStepHeading,
    isHideStepDetailsHeading,
    setIsHideStepDetailsHeading,
    isHideBomHeading,
    setIsHideBomHeading,

    // Editing states
    editingStepIndex,
    setEditingStepIndex,
    editingBomIndex,
    setEditingBomIndex,

    // Modal states
    isModalOpen,
    setModalOpen,
    comment,
    setComment,
    isPartsModalOpen,
    setPartsModalOpen,
    selectedPartIndex,
    setSelectedPartIndex,
    selectedTable,
    setSelectedTable,
    categoryModalOpenForBom,
    setCategoryModalOpenForBom,
    categoryModalOpenForPart,
    setCategoryModalOpenForPart,

    // Handlers
    handleShortDescriptionChange,
    handleHideStepHeadingChange,
    handleHideStepDetailsHeadingChange,
    handleHideBomHeadingChange,
    handleBomTableCategoryChange,
    handleBomTableCategoryClear,
    handlePartNumberCategoryChange,
    handlePartNumberCategoryClear,
    handleStepDetailsChange,
    handleBomTableChange,
    addStepRow,
    addBomRow,
    handleSelectPart,
    resetForm,
  };
};

export default useStepForm;
